import { useLayoutEffect, useRef } from 'react'
import { coachLauncherPreferences } from './coachLauncherPreferences'
import { useCoachLauncherPlacement } from './CoachLauncherPlacement'
import { coachSafeAreas, coachViewport } from './coachViewport'
import {
  clampLauncher,
  launcherBounds,
  restoreLauncher,
  snapLauncher,
  type LauncherPosition,
  type LauncherPreference,
} from './coachLauncherPosition'

const DRAG_THRESHOLD = 8
const KEYBOARD_STEP = 24
const SNAP_DURATION = 180

type Gesture = {
  pointerId: number
  start: LauncherPosition
  origin: LauncherPosition
  position: LauncherPosition
  dragging: boolean
}

export function useFloatingCoachLauncher(
  onToggle: () => void,
  isOpen: boolean,
  inMenu = false,
  inGameActions = false,
) {
  const launcherRef = useRef<HTMLButtonElement>(null)
  const toggleRef = useRef(onToggle)
  const placement = useCoachLauncherPlacement()
  const positionRevision = placement?.positionRevision ?? 0
  const readPosition = placement?.readPosition ?? coachLauncherPreferences.read
  const writePosition = placement?.writePosition ?? coachLauncherPreferences.write

  useLayoutEffect(() => {
    toggleRef.current = onToggle
  }, [onToggle])

  useLayoutEffect(() => {
    const button = launcherRef.current
    if (!button || isOpen || inMenu) return

    let preference = inGameActions ? null : readPosition()
    let gesture: Gesture | null = null
    let suppressClick = false
    let snapTimer: ReturnType<typeof setTimeout> | undefined
    const viewport = window.visualViewport

    const bounds = () =>
      launcherBounds(coachViewport(), button.getBoundingClientRect(), coachSafeAreas(button))

    const stopMotion = () => {
      clearTimeout(snapTimer)
      delete button.dataset.motion
    }

    const positionButton = (position: LauncherPosition) => {
      button.dataset.positioned = 'true'
      // Keep the same DOM node and its reserved dock while switching to fixed positioning.
      button.dataset.floating = 'true'
      const rect = button.getBoundingClientRect()
      // Convert the visual bounds back to the fixed CSS box, preserving the original tilt.
      const xOffset = rect.left - button.offsetLeft
      const yOffset = rect.top - button.offsetTop
      button.style.left = position.x - xOffset + 'px'
      button.style.top = position.y - yOffset + 'px'
      button.style.right = 'auto'
      button.style.bottom = 'auto'
    }

    const restore = () => {
      stopMotion()
      button.dataset.positioned = 'true'
      if (preference) positionButton(restoreLauncher(preference, bounds()))
      else {
        button.dataset.floating = 'false'
        for (const property of ['left', 'top', 'right', 'bottom']) {
          button.style.removeProperty(property)
        }
      }
    }

    const release = (pointerId: number) => {
      if (button.hasPointerCapture?.(pointerId)) button.releasePointerCapture(pointerId)
    }

    const cancel = () => {
      const active = gesture
      if (!active) return
      gesture = null
      suppressClick = true
      release(active.pointerId)
      restore()
    }

    const save = (next: LauncherPreference) => {
      preference = next
      writePosition(next)
      stopMotion()
      button.dataset.motion = 'snapping'
      positionButton(restoreLauncher(next, bounds()))
      snapTimer = setTimeout(stopMotion, SNAP_DURATION)
    }

    const touchStart = (event: TouchEvent) => {
      // The owned pointerup tap can hide this button beneath the newly opened panel.
      // Cancel compatibility mouse events before they can hit a panel action instead.
      event.preventDefault()
    }

    const pointerDown = (event: PointerEvent) => {
      if (gesture || !event.isPrimary || event.button !== 0) return
      // Suppress native mouse/pen image dragging; touch-action handles touch without cancelling taps.
      if (event.pointerType !== 'touch') event.preventDefault()
      button.focus({ preventScroll: true })
      stopMotion()
      suppressClick = false
      const rect = button.getBoundingClientRect()
      const origin = { x: rect.left, y: rect.top }
      gesture = {
        pointerId: event.pointerId,
        start: { x: event.clientX, y: event.clientY },
        origin,
        position: origin,
        dragging: false,
      }
      button.setPointerCapture(event.pointerId)
    }

    const updateGesture = (event: PointerEvent) => {
      const active = gesture
      if (!active || event.pointerId !== active.pointerId) return
      const dx = event.clientX - active.start.x
      const dy = event.clientY - active.start.y
      if (!active.dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
      active.dragging = true
      suppressClick = true
      if (inGameActions) return
      event.preventDefault()
      button.dataset.positioned = 'true'
      button.dataset.motion = 'dragging'
      active.position = clampLauncher(
        { x: active.origin.x + dx, y: active.origin.y + dy },
        bounds(),
      )
      positionButton(active.position)
    }

    const pointerUp = (event: PointerEvent) => {
      const active = gesture
      if (!active || event.pointerId !== active.pointerId) return
      // A fast release can arrive without an intermediate pointermove, or beyond the last one.
      updateGesture(event)
      gesture = null
      release(active.pointerId)
      if (active.dragging) {
        if (!inGameActions) save(snapLauncher(active.position, bounds()))
      } else if (event.pointerType === 'touch') {
        // Touch browsers may omit a compatibility click after a drag. Own the tap once,
        // then suppress its optional native click; keyboard/assistive clicks stay native.
        suppressClick = true
        toggleRef.current()
      }
    }

    const pointerCancel = (event: PointerEvent) => {
      if (event.pointerId === gesture?.pointerId) cancel()
    }

    const click = (event: MouseEvent) => {
      if (suppressClick && event.detail !== 0) {
        event.preventDefault()
        event.stopPropagation()
        suppressClick = false
        return
      }
      suppressClick = false
      toggleRef.current()
    }

    const keyDown = (event: KeyboardEvent) => {
      if (inGameActions) return
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return
      event.preventDefault()
      cancel()
      stopMotion()
      button.dataset.positioned = 'true'
      const rect = button.getBoundingClientRect()
      const area = bounds()
      const point = { x: rect.left, y: rect.top }
      if (event.key === 'ArrowLeft') point.x = area.left
      if (event.key === 'ArrowRight') point.x = area.right
      if (event.key === 'ArrowUp') point.y -= KEYBOARD_STEP
      if (event.key === 'ArrowDown') point.y += KEYBOARD_STEP
      const next = snapLauncher(point, area)
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        next.side = preference?.side ?? next.side
      }
      save(next)
    }

    const viewportChanged = () => {
      // A keyboard/rotation changes presentation only; never overwrite the logical preference.
      if (gesture) cancel()
      else restore()
    }

    button.addEventListener('touchstart', touchStart, { passive: false })
    button.addEventListener('pointerdown', pointerDown)
    button.addEventListener('pointermove', updateGesture)
    button.addEventListener('pointerup', pointerUp)
    button.addEventListener('pointercancel', pointerCancel)
    button.addEventListener('lostpointercapture', pointerCancel)
    button.addEventListener('click', click)
    button.addEventListener('keydown', keyDown)
    window.addEventListener('resize', viewportChanged)
    window.addEventListener('orientationchange', viewportChanged)
    viewport?.addEventListener('resize', viewportChanged)
    viewport?.addEventListener('scroll', viewportChanged)
    const buttonResized = () => {
      if (gesture?.dragging) {
        gesture.position = clampLauncher(gesture.position, bounds())
        positionButton(gesture.position)
      } else if (!gesture) restore()
    }
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(buttonResized)
    observer?.observe(button)
    restore()

    return () => {
      button.removeEventListener('touchstart', touchStart)
      button.removeEventListener('pointerdown', pointerDown)
      button.removeEventListener('pointermove', updateGesture)
      button.removeEventListener('pointerup', pointerUp)
      button.removeEventListener('pointercancel', pointerCancel)
      button.removeEventListener('lostpointercapture', pointerCancel)
      button.removeEventListener('click', click)
      button.removeEventListener('keydown', keyDown)
      window.removeEventListener('resize', viewportChanged)
      window.removeEventListener('orientationchange', viewportChanged)
      viewport?.removeEventListener('resize', viewportChanged)
      viewport?.removeEventListener('scroll', viewportChanged)
      observer?.disconnect()
      if (gesture) release(gesture.pointerId)
      gesture = null
      stopMotion()
    }
  }, [isOpen, inMenu, inGameActions, positionRevision, readPosition, writePosition])

  return launcherRef
}
